import React, { useCallback, useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { getIn } from 'formik'
import { useTranslation } from 'react-i18next'

import Button from '../../../../../../components/Button'
import AdPreviewSoundControlWrapper from '../../../../../../features/components/FacebookPreviews/reusablePreviewComponents/AdPreviewSoundControlWrapper'
import ErrorMessage from '../../../../../../components/Form/ErrorMessage'
import CarouselMediaFields from '../../../../../ReusableFormFields/MediaFields/CarouselMediaFields'
import FacebookCarouselAdPreview from '../../../../../../features/components/FacebookPreviews/FacebookCarouselAdPreview'

import useFlatFormikValues from '../../../useFlatFormikValues'

import { adCarouselSelectedIndexesSelector } from '../../../../../../modules/selectors/ads'

import {
  AD_PREVIEW_CAROUSEL_INDEX_KEY,
  CAROUSEL_ITEMS,
  CAROUSEL_ITEMS_LENGTH,
  CLICK_THROUGH_LINK,
  DESCRIPTION_ENTER,
  DESCRIPTION_OPTION,
  DESCRIPTIONS_LIST,
  HEADLINE_ENTER,
  HEADLINE_OPTION,
  HEADLINES_LIST
} from '../../../fields'
import { FILE_URL } from '../../../../../ReusableFormFields/AdFileUpload/fields'

import { AdFacebookCreateContext } from '../../../../formsContexts/AdFacebookCreateFormContextProvider'

import useStyles from '../../../../../../styles/common/stepForms'

// To make it possible for component to work with nested objects
// in formik values, use the built-in formik function getIn
const CarouselMediaStep = ({ formik, handleStepChange, isStepFieldsValid, isActive }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [showErrors, setShowErrors] = useState(false)

  const selectedCarouselIndexes = useSelector(adCarouselSelectedIndexesSelector)

  const { campaignObjective } = useContext(AdFacebookCreateContext)

  const { values, errors, setFieldTouched } = formik

  const flatFormikValues = useFlatFormikValues({ formikValues: values, allowUseSingleMediaItem: false })

  const carouselItemsLengthErrors = errors[CAROUSEL_ITEMS_LENGTH]

  const adPreviewCarouselIndexKey = values[AD_PREVIEW_CAROUSEL_INDEX_KEY]

  const selectedCarouselItemIndex = selectedCarouselIndexes[adPreviewCarouselIndexKey] || 0

  // Constant that represent path ot item in formik values
  const itemPath = `${CAROUSEL_ITEMS}[${selectedCarouselItemIndex}]`

  // This function set touched value for AD_HEADLINE and AD_DESCRIPTION fields to true
  // on next time continue button has been clicked if user checked those fields
  const setItemFieldsTouched = useCallback(() => {
    const headlineEnter = getIn(values, `${itemPath}.${HEADLINE_OPTION}`) === HEADLINE_ENTER
    const descriptionChecked = getIn(values, `${itemPath}.${DESCRIPTION_OPTION}`) === DESCRIPTION_ENTER

    if (headlineEnter) {
      setFieldTouched(`${itemPath}.${HEADLINES_LIST}[0].text`, true)
    }

    if (descriptionChecked) {
      setFieldTouched(`${itemPath}.${DESCRIPTIONS_LIST}[0].text`, true)
    }

    setFieldTouched(`${itemPath}.${CLICK_THROUGH_LINK}`, true)

    setFieldTouched(`${itemPath}.${FILE_URL}`, true)
  }, [setFieldTouched, values, itemPath])

  const handleContinue = useCallback(() => {
    handleStepChange([CAROUSEL_ITEMS, CAROUSEL_ITEMS_LENGTH], values)
    setItemFieldsTouched()
    setShowErrors(true)
  }, [handleStepChange, setItemFieldsTouched, values])

  return (
    <>
      <div className={classes.stepTitle}>{t('Select images or videos')}</div>
      <div className={classes.stepBody}>
        <AdPreviewSoundControlWrapper formikValues={flatFormikValues} isSoundDisabled={!isActive}>
          <FacebookCarouselAdPreview formikValues={flatFormikValues} elementInProgress={CAROUSEL_ITEMS} />
        </AdPreviewSoundControlWrapper>
        <CarouselMediaFields formik={formik} isFieldsValid={isStepFieldsValid} campaignObjective={campaignObjective} />
      </div>
      {showErrors && carouselItemsLengthErrors && <ErrorMessage error={carouselItemsLengthErrors} />}
      <div className={classes.stepFooter}>
        <Button type="button" className="dark" onClick={handleContinue}>
          {t('Continue')}
        </Button>
      </div>
    </>
  )
}

export default CarouselMediaStep
